import { lazy } from "react";
import {
  COMPANY_MANAGEMENT_ROLES,
  COST_CENTER_ROLES,
  CONTRACT_MANAGEMENT_ROLES,
  DATA_SOURCE_ROLES,
  DATA_SOURCE_MAPPING_ROLES,
  OWN_COMPANY_ROLES,
  EVALUATE_ROLES,
  PLANNER_ROLES,
  SERIES_ROLES,
  TASK_CREATE_ROLES,
  TASK_MANAGEMENT_ROLES,
  USER_MANAGEMENT_ROLES,
  USER_OVERVIEW_ROLES,
} from "@utils/rbac";
import { withSuspense, withTaskProvider, withPrivateRoute } from "./helpers";

const LoginPage = lazy(() => import("@pages/auth/login.tsx"));
const CompanyRegisterPage = lazy(() => import("@pages/auth/register.tsx"));
const SetPasswordPage = lazy(() => import("@pages/auth/set-password.tsx"));
const ResetPasswordPage = lazy(() => import("@pages/auth/reset-password.tsx"));
const UserCreatePage = lazy(() => import("@pages/users/create/index.tsx"));
const UserUpdatePage = lazy(() => import("@pages/users/update/index.tsx"));
const UserOverviewPage = lazy(() => import("@pages/users/overview/index.tsx"));
const MonthOverviewPage = lazy(() => import("@pages/users/month-overview/index.tsx"));
const CreateTaskPage = lazy(() => import("@pages/tasks/create-task/index.tsx"));
const TaskManagementPage = lazy(() => import("@pages/tasks/management/index.tsx"));
const TaskSeriesPage = lazy(() => import("@pages/tasks/series/index.tsx"));
const WorkPage = lazy(() => import("@pages/plan/index.tsx"));
const ArbeitenPage = lazy(() => import("@pages/arbeiten/index.tsx"));
const TimeEvaluationPage = lazy(() => import("@pages/time-evaluation/index.tsx"));
const WorkEvaluationPage = lazy(() => import("@pages/work-evaluation/index.tsx"));
const KostenstellenPage = lazy(() => import("@pages/kostenstellen/index.tsx"));
const EvaluatePageWrapper = lazy(() => import("@pages/evaluate/index.tsx"));
const DayDetailPage = lazy(() => import("@pages/evaluate/day/index.tsx"));
const VertragsverwaltungPage = lazy(
  () => import("@pages/vertragsverwaltung/index.tsx"),
);
const DataSourcePage = lazy(
  () => import("@pages/datenquelle/index.tsx"),
);
const DataSourceMappingPage = lazy(
  () => import("@pages/datenquellenverwaltung/index.tsx"),
);
const CompaniesPage = lazy(() => import("@pages/companies/index.tsx"));
const CompanyDetailPage = lazy(() => import("@pages/companies/detail/index.tsx"));
const CompanyBlockedPage = lazy(() => import("@pages/company-blocked/index.tsx"));
const UnternehmensdatenPage = lazy(
  () => import("@pages/unternehmensdaten/index.tsx"),
);

export const routes = [
  {
    path: "/login",
    element: withSuspense(<LoginPage />),
  },
  {
    path: "/register",
    element: withSuspense(<CompanyRegisterPage />),
  },
  {
    path: "/passwort-vergessen",
    element: withSuspense(<ResetPasswordPage />),
  },
  {
    path: "/passwort-festlegen/:token",
    element: withSuspense(<SetPasswordPage />),
  },
  {
    path: "/passwort-zuruecksetzen/:token",
    element: withSuspense(<SetPasswordPage reset={true} />),
  },
  {
    path: "/unternehmen-gesperrt",
    element: withSuspense(<CompanyBlockedPage />),
  },
  {
    path: "/",
    element: withPrivateRoute(withTaskProvider(<WorkPage />), PLANNER_ROLES),
  },
  {
    path: "/arbeiten",
    element: withPrivateRoute(<ArbeitenPage />, EVALUATE_ROLES),
  },
  {
    path: "/planen",
    element: withPrivateRoute(withTaskProvider(<WorkPage />), PLANNER_ROLES),
  },
  {
    path: "/benutzer/:id/auswerten",
    element: withPrivateRoute(<TimeEvaluationPage />),
  },
  {
    path: "/benutzer/:id/arbeitsauswertung",
    element: withPrivateRoute(<WorkEvaluationPage />),
  },
  {
    path: "/benutzer",
    element: withPrivateRoute(<UserOverviewPage />, USER_OVERVIEW_ROLES),
  },
  {
    path: "/benutzer/monatsuebersicht",
    element: withPrivateRoute(<MonthOverviewPage />, USER_OVERVIEW_ROLES),
  },
  {
    path: "/benutzer/neu",
    element: withPrivateRoute(<UserCreatePage />, USER_MANAGEMENT_ROLES),
  },
  {
    path: "/benutzer/:id",
    element: withPrivateRoute(<UserUpdatePage />, USER_OVERVIEW_ROLES),
  },
  {
    path: "/aufgabe/neu",
    element: withPrivateRoute(<CreateTaskPage />, TASK_CREATE_ROLES),
  },
  {
    path: "/aufgabe/:id",
    element: withPrivateRoute(<CreateTaskPage />),
  },
  {
    path: "/aufgaben",
    element: withPrivateRoute(<TaskManagementPage />, TASK_MANAGEMENT_ROLES),
  },
  {
    path: "/aufgaben/serien/:seriesId",
    element: withPrivateRoute(<TaskSeriesPage />, SERIES_ROLES),
  },
  {
    // "Serie ab Datum ändern": the task form in series mode. ?ab=YYYY-MM-DD
    // preselects the valid-from date.
    path: "/aufgaben/serien/:seriesId/bearbeiten",
    element: withPrivateRoute(<CreateTaskPage />, SERIES_ROLES),
  },
  {
    // One run by series and original series date, virtual or stored.
    // Opening it never stores it.
    path: "/aufgaben/serien/:seriesId/durchlauf/:occurrenceDate",
    element: withPrivateRoute(<CreateTaskPage />),
  },
  {
    path: "/kostenstellen",
    element: withPrivateRoute(<KostenstellenPage />, COST_CENTER_ROLES),
  },
  {
    path: "/auswerten",
    element: withPrivateRoute(<EvaluatePageWrapper />, EVALUATE_ROLES),
  },
  {
    // "Tagesdetail": one person's whole day, opened from the day card.
    path: "/auswerten/tag/:userId/:day",
    element: withPrivateRoute(<DayDetailPage />, EVALUATE_ROLES),
  },
  {
    path: "/vertragsverwaltung",
    element: withPrivateRoute(
      <VertragsverwaltungPage />,
      CONTRACT_MANAGEMENT_ROLES,
    ),
  },
  {
    path: "/datenquelle",
    element: withPrivateRoute(<DataSourcePage />, DATA_SOURCE_ROLES),
  },
  {
    path: "/datenquellenverwaltung",
    element: withPrivateRoute(<DataSourceMappingPage />, DATA_SOURCE_MAPPING_ROLES),
  },
  {
    // The company Admin's own view. Distinct from "/unternehmen", which is
    // the Super Admin's cross-tenant list.
    path: "/unternehmensdaten",
    element: withPrivateRoute(<UnternehmensdatenPage />, OWN_COMPANY_ROLES),
  },
  {
    path: "/unternehmen",
    element: withPrivateRoute(<CompaniesPage />, COMPANY_MANAGEMENT_ROLES),
  },
  {
    path: "/unternehmen/:id",
    element: withPrivateRoute(<CompanyDetailPage />, COMPANY_MANAGEMENT_ROLES),
  },
];
